// Formatting only — no business rules. Play money carries the market's play
// symbol (S₦, S₵ …) when the catalogue is loaded; real money is *_minor / 100
// in the order's currency.

export const MARKETS = ["NG", "GH", "KE", "ZA", "US", "GB", "CA", "IN", "CN", "EG"] as const;
export const MARKET_NAMES: Record<string, string> = {
  NG: "Nigeria",
  GH: "Ghana",
  KE: "Kenya",
  ZA: "South Africa",
  US: "United States",
  GB: "United Kingdom",
  CA: "Canada",
  IN: "India",
  CN: "China",
  EG: "Egypt",
};
export const MARKET_CURRENCY: Record<string, string> = { NG: "NGN", GH: "GHS", KE: "KES", ZA: "ZAR", US: "USD", GB: "GBP", CA: "CAD", IN: "INR", CN: "CNY", EG: "EGP" };
// The content CMS's cities: one per market, its home city (the server's cityMarket in internal/admin/liveops.go).
export const CITIES: { city: string; market: string; name: string }[] = [
  { city: "lagos", market: "NG", name: "Lagos" },
  { city: "accra", market: "GH", name: "Accra" },
  { city: "nairobi", market: "KE", name: "Nairobi" },
  { city: "johannesburg", market: "ZA", name: "Johannesburg" },
  { city: "houston", market: "US", name: "Houston" },
  { city: "london", market: "GB", name: "London" },
  { city: "toronto", market: "CA", name: "Toronto" },
  { city: "mumbai", market: "IN", name: "Mumbai" },
  { city: "shanghai", market: "CN", name: "Shanghai" },
  { city: "cairo", market: "EG", name: "Cairo" },
];
export const PROVIDERS = ["paystack", "flutterwave", "bachs", "stripe", "stripe_usdc", "vasp", "appstore", "playstore"] as const;

const nf = new Intl.NumberFormat("en-US");

export function num(n: unknown, digits = 0): string {
  const v = typeof n === "string" ? Number(n) : (n as number);
  if (v === null || v === undefined || Number.isNaN(v)) return "—";
  if (digits) return v.toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits });
  return nf.format(Math.round(v));
}

export function compact(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  if (Math.abs(n) < 10000) return nf.format(Math.round(n));
  return new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 }).format(n);
}

export function pct(n: number | null | undefined, digits = 1): string {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return (n * 100).toFixed(digits) + "%";
}

/** Play amount: "S₦ 5,000,000" with a symbol, "5,000,000" without. */
export function play(n: number | null | undefined, symbol?: string | null): string {
  if (n === null || n === undefined) return "—";
  const s = nf.format(Math.round(n));
  return symbol ? `${symbol} ${s}` : s;
}

const moneyFmt = new Map<string, Intl.NumberFormat>();
/** Real money from minor units: 500000 NGN → "₦5,000.00". */
export function money(minor: number | null | undefined, currency: string | null | undefined): string {
  if (minor === null || minor === undefined || Number.isNaN(minor)) return "—";
  const cur = (currency || "").toUpperCase();
  if (!cur) return num(minor / 100, 2);
  let f = moneyFmt.get(cur);
  if (!f) {
    try {
      // CAD and CNY keep their country mark (CA$, CN¥) so they never read as US dollars or yen.
      f = new Intl.NumberFormat("en-US", { style: "currency", currency: cur, currencyDisplay: cur === "CAD" || cur === "CNY" ? "symbol" : "narrowSymbol" });
    } catch {
      f = new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    moneyFmt.set(cur, f);
  }
  return f.format(minor / 100);
}

const dtFmt = new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
const dFmt = new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "short", year: "numeric", timeZone: "UTC" });

export function dateTime(iso: unknown): string {
  if (!iso || typeof iso !== "string") return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso) : dtFmt.format(d);
}

/** UTC calendar day (cups, pulse, index and revenue days are UTC). */
export function day(iso: unknown): string {
  if (!iso || typeof iso !== "string") return "—";
  const d = new Date(iso.length === 10 ? iso + "T00:00:00Z" : iso);
  return Number.isNaN(d.getTime()) ? String(iso) : dFmt.format(d);
}

export function isoDay(d: Date = new Date()): string {
  return d.toISOString().slice(0, 10);
}

export function addDays(d: Date, n: number): Date {
  const x = new Date(d.getTime());
  x.setUTCDate(x.getUTCDate() + n);
  return x;
}

export function ago(iso: unknown): string {
  if (!iso || typeof iso !== "string") return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return String(iso);
  const s = Math.round((Date.now() - t) / 1000);
  const fut = s < 0;
  const a = Math.abs(s);
  let out: string;
  if (a < 60) out = a + "s";
  else if (a < 3600) out = Math.round(a / 60) + "m";
  else if (a < 86400) out = Math.round(a / 3600) + "h";
  else out = Math.round(a / 86400) + "d";
  return fut ? "in " + out : out + " ago";
}

export function shortId(id: unknown): string {
  if (!id || typeof id !== "string") return "—";
  return id.length > 12 ? id.slice(0, 8) + "…" : id;
}

/** Masks the host part of an IP (and drops the port) until revealed. */
export function maskIp(ip: unknown): string {
  if (!ip || typeof ip !== "string") return "—";
  // Audit rows keep a keyed hash of the address (ADR 0015); older rows hold the address itself.
  if (/^[0-9a-f]{64}$/.test(ip)) return `#${ip.slice(0, 8)}`;
  const host = ip.replace(/:\d+$/, "");
  if (host.includes(".")) return host.split(".").slice(0, 2).join(".") + ".•.•";
  return host.split(":").slice(0, 2).join(":") + ":••••";
}

export function humanize(s: string): string {
  return s.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}

export function json(v: unknown): string {
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

export function oneLine(v: unknown, max = 160): string {
  let s: string;
  try {
    s = typeof v === "string" ? v : JSON.stringify(v);
  } catch {
    s = String(v);
  }
  if (s === undefined || s === null) return "";
  return s.length > max ? s.slice(0, max - 1) + "…" : s;
}

/** Major-unit text ("5,000.50") → minor units, or null when empty/invalid. */
export function toMinor(text: string): number | null {
  const t = text.replace(/,/g, "").trim();
  if (!t) return null;
  const n = Number(t);
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100);
}

export function parseIntSafe(text: string): number | null {
  const t = text.replace(/[,\s_]/g, "").trim();
  if (!/^-?\d+$/.test(t)) return null;
  return Number(t);
}

/** The venues table games are played in (internal/game/ads.go ArenaVenues): what arena_screen:<venue> and arena_boards:<venue> take. */
export const ARENA_VENUES = [
  { key: "rooftop", name: "The Rooftop (chess, checkers, reversi)" },
  { key: "club", name: "Neon Lounge (Whot and the card games)" },
  { key: "beach", name: "Lagoon Beach Club (Ludo, snakes and ladders)" },
  { key: "courtyard", name: "The Courtyard (dominoes, mancala, backgammon)" },
  { key: "stadium", name: "SoftLife Arena (Connect 4, tic-tac-toe, gomoku)" },
] as const;
